import { useQuery, gql } from '@apollo/client';

/**
 * `limit: 1` — the menu only needs to know whether there is *any*, and the full page fetches
 * its own list anyway. Without the limit this would pull fifty posts on every render of the
 * navigation bar to answer a yes/no question.
 */
const HAS_POSTS = gql`
  query HasPosts {
    myPosts(limit: 1) {
      id
    }
  }
`;

/**
 * Has this person written anything?
 *
 * The *My posts* entry is hidden until they have, because a menu item leading to an empty
 * state is a permanent reminder of a feature somebody does not use. It appears the moment
 * their first post exists, which is also the moment it starts being worth opening.
 *
 * Spans every organisation they belong to, matching `myPosts` itself — what you wrote is a
 * property of your memberships, not of a current organisation.
 *
 * **This is the one nav entry that deliberately ignores the working organisation**, and it was
 * checked rather than overlooked when the rest of the nav was made data-driven. A post written
 * in organisation A makes the entry appear while working in organisation B — but unlike the
 * gallery case, the destination is not empty: the page lists every organisation's posts and
 * labels each with the one it belongs to. Narrowing this to the working organisation would mean
 * teaching a deliberately cross-organisation feature to pretend it is not one.
 */
export const useHasPosts = (): boolean => {
  const { data, loading, error } = useQuery(HAS_POSTS);
  if (loading || error) return false;
  return (data?.myPosts?.length ?? 0) > 0;
};
